// GridProduto.js — Página /livros.
// Layout:
//   - Categorias num POPUP, no cabeçalho (11/09/2026). Antes eram uma coluna
//     fixa de 260px à esquerda da grade; o dono pediu o popup do acervo do
//     Clube aqui também, e a grade ficou com a largura inteira. Preço e
//     Disponibilidade saíram em 09/09/2026, Tipo em 10/09/2026: um catálogo de
//     200 títulos quase todo "Físico e Ebook" transformava o filtro numa
//     pergunta cuja resposta era sempre a mesma lista. Sobrou a única que de
//     fato reparte o acervo.
//   - No celular o mesmo botão abre um bottom-sheet (ver PainelFiltros).
//   - A faixa "Resultados": contagem + busca + o botão de Categorias +
//     chips, que ACOMPANHA O SCROLL no desktop (ver GRUDA_NO_TOPO).
//   - Grid do card único do site (CardDestaque), paginado.
//   - Estado vazio com ilustração isométrica "Nada encontrado para X".
// Padrões: nenhum filtro pré-marcado; ordenação padrão "Mais Populares".
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import CardDestaque from "../cardDestaque/CardDestaque";
import { BotaoCategorias, DrawerFiltros } from "./PainelFiltros";
import CabecalhoAcervo from "./CabecalhoAcervo";
import QuickView from "./QuickView";
import Pagination from "../../utils/Pagination";
import RevealStagger from "../../ui/RevealStagger";
import { GradeSkeleton } from "../../ui/CardSkeleton";
import CaixaVazia from "../../ui/CaixaVazia";
import useClube from "../../../hooks/useClube";
import useClubeVitrine from "../../../hooks/useClubeVitrine";
import useBarraGrudada, { CLASSES_ESCONDIDA, CLASSES_SUMICO } from "../../../hooks/useBarraGrudada";

// Quantos livros cabem numa página. 12 fecha TODAS as larguras da grade - 2, 3,
// 4 e 6 colunas são todas divisores de 12 -, então nenhuma página termina com
// uma fileira pela metade. Foi por isso que a coluna a mais que a saída da
// sidebar liberou virou a SEXTA, e não a quinta: com cinco, a última fileira
// das telas largas mostraria dois cards perdidos num vão de cinco.
//
// AQUI ANTES HAVIA "Carregar mais", e o comentário que o defendia dizia que a
// paginação numérica tinha saído porque o número da página nunca esteve na URL
// — páginas que não dá para compartilhar nem indexar. A crítica era certa; o
// remédio é que estava errado. Agora a página MORA na URL (?pagina=), então ela
// volta pelo botão do navegador, cola num link e é vista pelo buscador. O
// recorte continua sendo local: o catálogo inteiro chega numa requisição só e é
// filtrado no navegador, então trocar de página não custa nada ao servidor.
//
// O que se perdeu com a troca: quem estava garimpando não empilha mais tudo
// numa rolagem só. Em compensação, ninguém mais precisa clicar seis vezes para
// ver o fim do acervo, e a posição na lista deixou de sumir a cada volta.
const LIVROS_POR_PAGINA = 12;

// Classes do bloco que gruda no desktop: a faixa de cabeçalho do conteúdo.
// Eram DOIS — a coluna de filtros grudava do lado dela — até as categorias
// virarem popup em 11/09/2026. A constante ficou porque a conta é longa demais
// para ser repetida de cabeça (ver --topo-fixo, mais abaixo).
//
// A transição de `top` copia a do cabeçalho do site (App.css: 380ms,
// cubic-bezier(0.4, 0, 0.2, 1), que é o `ease-in-out` do Tailwind). Sem ela o
// bloco daria um salto de 184px enquanto o cabeçalho ainda estava deslizando.
//
// E ELA SOME QUANDO O CABEÇALHO DO SITE VOLTA (pedido do dono, 11/09/2026).
// Rolando para cima, o cabeçalho reaparece e as duas faixas se empilhavam no
// topo, comendo um terço da tela justamente no gesto de voltar. A regra é a
// mesma da barra do perfil e da estante: com o cabeçalho à vista, a faixa
// grudada sai de cena; ela só assume o topo quando ele sai (useBarraGrudada).
// `CLASSES_SUMICO` já traz a transição de `top` junto com a de transform e
// opacity — duas classes `transition-[...]` no mesmo elemento brigariam, e
// quem ganhasse na ordem do CSS apagaria a outra.
const GRUDA_NO_TOPO =
  "md:sticky md:top-[var(--topo-fixo)] md:self-start md:z-30 " +
  CLASSES_SUMICO;

// ============== PREÇO, DISPONIBILIDADE E TIPO SAÍRAM (09-10/09/2026) ==============
//
// A pedido do cliente. Eram quatro faixas de preço e uma caixa "exibir produtos
// sem estoque", e as duas saíram INTEIRAS — a marcação e a regra que elas
// controlavam.
//
// A consequência que precisa estar escrita, porque não é óbvia: junto com a
// caixa de disponibilidade saiu o `if (estoque <= 0) return false`. O catálogo
// passou a mostrar TODOS os títulos, esgotado incluído. É o comportamento certo
// para uma tela sem o controle: filtro que esconde livro sem deixar ninguém
// revelá-lo é pior do que filtro nenhum — e o esgotado ainda vende como e-book,
// que é o que a página do produto já resolve.
//
// Para reverter, o filtro antigo está em 49e9309.

const ORDENS = [
  { key: "populares",  label: "Mais Populares" },
  { key: "recentes",   label: "Mais Recentes" },
  { key: "preco-asc",  label: "Menor Preço" },
  { key: "preco-desc", label: "Maior Preço" },
];

// Nome de categoria (string ou objeto { nome })
const nomeCategoria = (c) => (typeof c === "string" ? c : c?.nome);

// Métrica de popularidade defensiva (cobre variações da API)
const popularidadeDe = (e) =>
  Number(e.popularidade ?? e.vendas ?? e.total_vendas ?? e.vendas_total ?? e.views ?? 0);

// A frase da barra do Clube, montada com o que o servidor mandou.
//
// O PREÇO NÃO É ESCRITO AQUI, e isso não é preciosismo: o dia em que a
// assinatura mudar de valor, a tela que ficou com o número velho é a que custa
// a venda — e esta é justamente a que o visitante anônimo mais vê. `precoCentavos`
// e `dias` chegam de `clube/vitrine` (ver useClubeVitrine), a mesma fonte que a
// página do Clube usa.
//
// Aqui os centavos aparecem ("R$ 50,00", não "R$ 50"): é a frase aprovada pelo
// cliente, palavra por palavra, e é assim que ela foi escrita.
//
// 30 dias vira "/mês" porque é como a pessoa pensa o prazo; qualquer outro
// prazo é dito em dias, senão a frase mentiria sobre o que ela está pagando.
const fraseDoClube = (centavos, dias) => {
  const preco = "R$ " + ((Number(centavos) || 0) / 100).toFixed(2).replace(".", ",");
  const ciclo = dias === 30 ? "/mês" : ` a cada ${dias} dias`;
  return `Leia todos os livros do nosso catálogo por apenas ${preco}${ciclo}`;
};

function GridProdutos() {
  const main = process.env.REACT_APP_MAIN;
  // A faixa de "Resultados" some quando o cabeçalho do site volta (ver
  // GRUDA_NO_TOPO). `margemNegativa: 16` é o `md:-mt-4` dela: a faixa nasce
  // 16px acima da sentinela.
  const {
    barraRef: faixaRef,
    sentinelaRef: faixaSentinelaRef,
    esconder: esconderFaixa,
  } = useBarraGrudada({ margemNegativa: 16 });
  // A linha do Clube some para quem ja assina; os numeros dela sao publicos e
  // vem sem login, porque o catalogo e a tela mais vista por visitante anonimo.
  //
  // `carregando` IMPORTA: saber se a pessoa assina custa uma ida ao servidor, e
  // ate ela voltar `assinante` e false. Sem esperar, o assinante via a barra
  // aparecer e sumir a cada visita ao catalogo, empurrando a grade ~66px para
  // baixo e de volta. Oferecer o clube a quem ja paga e o jeito mais rapido de
  // a oferta virar ruido; oferecer piscando e pior.
  const { assinante, carregando: carregandoClube } = useClube();
  const vitrine = useClubeVitrine();
  const API_URL = process.env.REACT_APP_API;
  const location = useLocation();
  const navigate = useNavigate();

  const [ebooks, setEbooks] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);

  // ---- Filtros (estado) ----
  const params = new URLSearchParams(location.search);
  const buscaURL = params.get("busca") || "";
  const categoriaURL = params.get("categoria") || "";
  const ordemURL = params.get("ordem") || "recentes";
  // A página é lida da URL, e não de um `useState`, porque é a URL que o botão
  // "voltar" restaura. Guardada em estado, voltar do livro para o catálogo (ou
  // desfazer um clique na paginação) devolvia sempre a página 1 — o defeito que
  // fazia a pessoa perder o lugar depois de rolar meio acervo.
  const paginaURL = parseInt(params.get("pagina"), 10) || 1;

  const [categoriaSel, setCategoriaSel] = useState(categoriaURL);
  // Nenhum filtro pré-marcado
  // O texto da barrinha que filtra a LISTA DE CATEGORIAS — não os livros. São
  // buscas diferentes e não podem dividir estado: esta mora só no painel, não
  // vai para a URL e some ao fechar o bottom-sheet, enquanto a busca de livros
  // (`buscaURL`) é compartilhável e vem do cabeçalho do site.
  const [buscaCategoria, setBuscaCategoria] = useState("");
  // A busca DESTA página. O valor que filtra é o da URL (`busca`), o mesmo
  // parâmetro que a barra do cabeçalho usa — as duas escrevem no mesmo lugar,
  // então nunca discordam. O estado local existe só para o cursor não pular
  // enquanto se digita.
  const [textoBusca, setTextoBusca] = useState(buscaURL);
  const [ordem, setOrdem] = useState(ORDENS.some((o) => o.key === ordemURL) ? ordemURL : "recentes");
  const [painelAberto, setPainelAberto] = useState(false); // bottom-sheet do celular
  const [espiando, setEspiando] = useState(null); // livro aberto na espiada rápida

  // Sincroniza categoria/ordem quando a URL muda (clique vindo do header)
  useEffect(() => {
    setCategoriaSel(categoriaURL);
    setOrdem(ORDENS.some((o) => o.key === ordemURL) ? ordemURL : "recentes");
  }, [categoriaURL, ordemURL]);

  // Busca ebooks + categorias
  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);
        const [rEbook, rCat] = await Promise.all([
          fetch(API_URL + "ebook/"),
          fetch(API_URL + "categorias"),
        ]);
        if (rEbook.ok) setEbooks(await rEbook.json());
        if (rCat.ok) setCategorias(await rCat.json());
      } catch (e) {
        console.error("Erro ao buscar livros/categorias:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [API_URL]);

  // Atualiza URL ao mudar categoria
  const setCategoria = (c) => {
    setCategoriaSel(c);
    const p = new URLSearchParams(location.search);
    if (c) p.set("categoria", c); else p.delete("categoria");
    navigate(`${location.pathname}?${p.toString()}`, { replace: true });
  };

  // O PainelFiltros recebe só os NOMES: quem sabe que a API pode devolver
  // string ou { nome } é esta página, não a camada de apresentação.
  const nomesCategorias = useMemo(
    () => categorias.map(nomeCategoria).filter(Boolean),
    [categorias]
  );

  // ====== Aplicação dos filtros ======
  //
  // A CATEGORIA FICA DE FORA DESTA LISTA, de propósito. `semCategoria` é o
  // conjunto que passou por tudo MENOS pela categoria, e é exatamente ele que a
  // busca facetada precisa: a contagem ao lado de "Direito" responde "quantos
  // livros eu veria SE clicasse aqui", e para isso ela não pode já estar
  // filtrada pela categoria que está selecionada agora.
  //
  // Separar assim é o que impede as duas contas de divergirem. A alternativa —
  // um segundo filtro escrito à mão só para as facetas — daria um número na
  // etiqueta e outro na grade no primeiro dia em que alguém mexesse num dos
  // dois, e ninguém desconfiaria da etiqueta.
  const semCategoria = useMemo(() => {
    const t = buscaURL ? buscaURL.toLowerCase() : "";

    return ebooks.filter((e) => {
      // Busca (mantém comportamento da URL antiga)
      if (t) {
        const titulo = e.titulo?.toLowerCase() || "";
        const autores = Array.isArray(e.autores)
          ? e.autores.map((a) => (typeof a === "object" ? a?.nome : a)).join(" ").toLowerCase()
          : "";
        if (!titulo.includes(t) && !autores.includes(t)) return false;
      }
      return true;
    });
  }, [ebooks, buscaURL]);

  // ===== OS NÚMEROS DO ACERVO (para o cabeçalho da página) =====
  //
  // Contados do catálogo INTEIRO, e não do recorte na tela: eles são uma frase
  // sobre a editora ("publicamos 199 livros em 24 áreas"), não sobre o filtro
  // que a pessoa acabou de ligar. Quem descreve a tela é a faixa de Resultados,
  // logo abaixo, que já diz "mostrando 1-12 de 37".
  //
  // ÁREAS conta só categoria COM LIVRO: `/categorias` devolve o cadastro, e
  // categoria cadastrada e nunca usada não é uma área em que a editora publica.
  //
  // AUTORES é por nome normalizado (sem acento, sem caixa, sem espaço dobrado).
  // O limite é conhecido: duas grafias do mesmo autor contam como dois. Preferi
  // errar para menos precisão a inventar um número redondo — e a normalização
  // já resolve o caso comum, que é acento digitado de formas diferentes.
  const numerosDoAcervo = useMemo(() => {
    const areas = new Set();
    const autores = new Set();

    ebooks.forEach((e) => {
      (Array.isArray(e.categorias) ? e.categorias : []).forEach((c) => {
        const nome = nomeCategoria(c);
        if (nome) areas.add(nome);
      });
      (Array.isArray(e.autores) ? e.autores : []).forEach((a) => {
        const nome = typeof a === "object" ? a?.nome : a;
        const chave = String(nome || "")
          .normalize("NFD")
          // Os acentos que o NFD separou, pela FAIXA ESCAPADA e não pelos
          // caracteres literais: combinantes crus num arquivo-fonte são
          // invisíveis, grudam no colchete anterior em qualquer editor e o
          // primeiro "arrumar o arquivo" os perde sem ninguém notar.
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/\s+/g, " ")
          .trim()
          .toLowerCase();
        if (chave) autores.add(chave);
      });
    });

    return { livros: ebooks.length, areas: areas.size, autores: autores.size };
  }, [ebooks]);

  // Quantos livros cada categoria tem, DENTRO do que já está filtrado. É o
  // número que aparece na etiqueta de cada linha do painel.
  //
  // Um livro em três categorias conta em três — não é o total da grade
  // repartido, é "o que eu veria em cada caminho". Somar as etiquetas dá mais
  // que o total, e isso é o esperado numa faceta.
  const contagemPorCategoria = useMemo(() => {
    const conta = new Map();
    semCategoria.forEach((e) => {
      const cats = Array.isArray(e.categorias) ? e.categorias : [];
      // `Set` porque o mesmo livro pode vir com a categoria repetida no
      // cadastro, e aí ele contaria duas vezes na própria etiqueta.
      new Set(cats.map(nomeCategoria).filter(Boolean)).forEach((nome) => {
        conta.set(nome, (conta.get(nome) || 0) + 1);
      });
    });
    return conta;
  }, [semCategoria]);

  const filtrados = useMemo(() => {
    if (!categoriaSel) return semCategoria;
    return semCategoria.filter((e) => {
      const cats = Array.isArray(e.categorias) ? e.categorias : [];
      return cats.some((c) => nomeCategoria(c) === categoriaSel);
    });
  }, [semCategoria, categoriaSel]);

  // Tudo o que os dois painéis (popup e bottom-sheet) precisam, num pacote só.
  // É de propósito que os dois leem o MESMO objeto: qualquer estado que
  // nascesse dentro do PainelFiltros seria uma segunda fonte de verdade, e o
  // desencontro só apareceria ao cruzar 768px (girar um tablet).
  //
  // MORA DEPOIS DOS MEMOS, e não junto dos outros preparos lá em cima: ele LÊ
  // `semCategoria` e `contagemPorCategoria`, que são `const`. Declarado antes,
  // o objeto era montado dentro da zona morta temporal das duas e a página
  // inteira caía com "Cannot access 'semCategoria' before initialization" —
  // ESLint avisa isso como `no-use-before-define`, que em CRA é só um aviso
  // amarelo no build e um erro em branco no navegador.
  const propsFiltros = {
    categorias: nomesCategorias,
    categoriaSel,
    aoEscolherCategoria: setCategoria,
    contagemPorCategoria,
    // O total das facetas é o conjunto SEM categoria — é o que a linha "Todos"
    // mostra, e é para onde a pessoa volta ao limpar a categoria.
    totalSemCategoria: semCategoria.length,
    buscaCategoria,
    aoBuscarCategoria: setBuscaCategoria,
  };

  // Ordenação
  const ordenados = useMemo(() => {
    const arr = [...filtrados];
    if (ordem === "preco-asc")  arr.sort((a, b) => (parseFloat(a.preco)||0) - (parseFloat(b.preco)||0));
    if (ordem === "preco-desc") arr.sort((a, b) => (parseFloat(b.preco)||0) - (parseFloat(a.preco)||0));
    // ===== "MAIS RECENTES" É POR DATA, E NÃO PELA ORDEM DA LISTA =====
    //
    // Era `arr.reverse()`: a API devolve o catálogo por id, então "recentes"
    // significava "id maior". Livro antigo recadastrado hoje ia para o topo, e
    // mudar a data de publicação não tirava ele de lá — foi o que o dono pediu
    // em 18/09/2026 para os livros dos dois primeiros congressos.
    // O id continua como desempate: dois livros cadastrados no mesmo instante
    // precisam de uma ordem estável, senão a grade embaralha a cada render.
    if (ordem === "recentes") {
      const quando = (e) => {
        const d = Date.parse(e.created_at || "");
        return isNaN(d) ? 0 : d;
      };
      arr.sort((a, b) => quando(b) - quando(a) || (Number(b.id) || 0) - (Number(a.id) || 0));
    }
    if (ordem === "populares")  arr.sort((a, b) => popularidadeDe(b) - popularidadeDe(a));
    return arr;
  }, [filtrados, ordem]);

  // ====== Paginação (recorte local, número na URL) ======
  const totalPaginas = Math.max(1, Math.ceil(ordenados.length / LIVROS_POR_PAGINA));
  // Página fora da faixa cai na PRIMEIRA, não na última: ?pagina=99 quase sempre
  // é link velho de um filtro que encolheu, e a primeira é a única resposta que
  // nunca está errada. A conta é derivada a cada render, e não guardada em
  // estado, porque enquanto o catálogo carrega `totalPaginas` ainda é 1 — assim
  // ela se conserta sozinha quando os livros chegam, em vez de travar na 1.
  const paginaAtual = paginaURL >= 1 && paginaURL <= totalPaginas ? paginaURL : 1;
  const inicio = (paginaAtual - 1) * LIVROS_POR_PAGINA;
  const itensPagina = ordenados.slice(inicio, inicio + LIVROS_POR_PAGINA);

  // Trocar de página EMPILHA no histórico (sem `replace`) — é o que faz o botão
  // "voltar" do navegador devolver a página anterior. Os filtros continuam com
  // `replace`, porque lá cada clique no histórico seria só ruído.
  const irParaPagina = (n) => {
    const p = new URLSearchParams(location.search);
    if (n > 1) p.set("pagina", String(n)); else p.delete("pagina");
    navigate(`${location.pathname}${p.toString() ? "?" + p.toString() : ""}`);
  };

  // Filtro, busca ou ordem novos invalidam a página em que a pessoa estava:
  // filtrar da página 7 com três páginas de resultado mostraria grade vazia.
  // `primeiroRender` existe para a MONTAGEM não limpar nada — sem ele, um link
  // com ?pagina=3 abriria sempre na 1, que é o defeito que a página na URL veio
  // corrigir.
  const primeiroRender = useRef(true);
  useEffect(() => {
    if (primeiroRender.current) { primeiroRender.current = false; return; }
    const p = new URLSearchParams(window.location.search);
    if (!p.has("pagina")) return;
    p.delete("pagina");
    navigate(`${location.pathname}${p.toString() ? "?" + p.toString() : ""}`, { replace: true });
  }, [categoriaSel, buscaURL, ordem, location.pathname, navigate]);


  // ====== Chips dos filtros aplicados ======
  // Antes não havia nenhuma pista do que estava filtrado e o resultado "sumia"
  // sem explicação. Cada chip remove o próprio filtro. Eles moram na faixa que
  // gruda no topo (ver o JSX), então continuam à mão no meio da rolagem — que
  // era o buraco que sobrava quando só a sidebar acompanhava o scroll.
  const chips = [];
  if (categoriaSel) chips.push({ id: "cat", texto: categoriaSel, limpar: () => setCategoria("") });
  if (buscaURL) {
    chips.push({
      id: "busca",
      texto: `Busca: "${buscaURL}"`,
      limpar: () => {
        setTextoBusca("");
        const q = new URLSearchParams(location.search);
        q.delete("busca");
        navigate(`${location.pathname}${q.toString() ? "?" + q.toString() : ""}`, { replace: true });
      },
    });
  }
  // AQUI HAVIA `filtrosAtivos`, o contador redondo do botão "Filtrar". Ele
  // saiu junto com o botão: o de Categorias mostra o NOME da categoria escolhida,
  // que diz tudo o que o "1" dizia e mais qual.

  const limparTudo = () => {
    // A barrinha de busca de categoria zera junto: ela é parte do painel, e
    // deixá-la escrita depois de "limpar tudo" esconderia categorias que
    // voltaram a ter livros.
    setBuscaCategoria("");
    // O campo desta página esvazia junto: limpar a URL e deixar a palavra
    // escrita no campo é o jeito mais rápido de a pessoa achar que não limpou.
    setTextoBusca("");
    setCategoriaSel("");
    const q = new URLSearchParams(location.search);
    q.delete("categoria");
    q.delete("busca");
    navigate(`${location.pathname}${q.toString() ? "?" + q.toString() : ""}`, { replace: true });
  };

  return (
    <div className="bg-insigne-bg fundo-abertura min-h-screen pt-[var(--nav-h)] px-4 md:px-8 pb-12 text-white">
      <div className="max-w-[1280px] mx-auto">
        {/* Breadcrumb */}
        <nav className="text-xs text-white/60 mb-6 flex gap-2">
          {/* Mesmo "Início" do cabeçalho — e agora com o mesmo sublinhado que
              cresce do centro. Sem cursor a regra é inócua: o traço nasce com
              largura zero e só cresce em :hover/:focus-visible. */}
          <Link to={main} className="link-sublinhado hover:!text-insigne-orange !text-white/60">Início</Link>
          <span>/</span>
          <span className="text-insigne-orange">Livros</span>
        </nav>

        {/* A APRESENTAÇÃO DA PÁGINA. Fora da faixa que gruda de propósito —
            ver o cabeçalho de CabecalhoAcervo.js. Ela vem ANTES da barra do
            Clube porque uma página precisa se apresentar antes de oferecer
            assinatura: a ordem inversa vendia antes de dizer o que era. */}
        <CabecalhoAcervo
          totalDeLivros={numerosDoAcervo.livros}
          totalDeAreas={numerosDoAcervo.areas}
          totalDeAutores={numerosDoAcervo.autores}
          totalVisivel={semCategoria.length}
          categorias={nomesCategorias}
          contagemPorCategoria={contagemPorCategoria}
          categoriaSel={categoriaSel}
          aoEscolherCategoria={setCategoria}
          // "ver todas" abre a MESMA folha do celular, em qualquer largura: ela
          // já tem a lista inteira, a busca por nome e as contagens, e não é
          // escondida por CSS acima de 768px (ver o comentário em
          // DrawerFiltros). O popup do botão de Categorias guarda o estado
          // dele por dentro, então abri-lo daqui exigiria erguer aquele estado
          // para cá só por causa deste link.
          aoVerTodasAsAreas={() => setPainelAberto(true)}
          carregando={loading}
        />

        {/* ============ O CLUBE, UMA VEZ SÓ ============
            A auditoria pedia um selo "incluso no Clube" NO CARD de cada livro.
            Ele não foi feito, e não por falta de tempo: a regra do clube hoje é
            "assinante lê todo o acervo", então o selo apareceria em praticamente
            todos os cards. Selo que está em tudo não distingue nada — vira
            textura visual, e ainda rouba espaço do preço, que é a informação
            pela qual o card existe.

            A mesma informação, dita uma vez no alto do catálogo, comunica mais
            e não custa nada ao card. Quando (e se) existirem títulos FORA da
            assinatura, o selo por livro passa a fazer sentido e precisará de
            uma coluna `incluso_no_clube` para ser verdade — hoje ele seria só
            uma repetição bonita.

            Some sozinho para quem já assina e quando o servidor não mandou os
            números: oferecer o clube a quem já paga é o jeito mais rápido de a
            oferta virar ruído. `dias` entra na condição junto com o preço porque
            a frase precisa dos dois — sem o prazo ela sairia "a cada 0 dias". */}
        {!carregandoClube && !assinante && vitrine.acervo > 0 &&
          vitrine.precoCentavos > 0 && vitrine.dias > 0 && (
          <Link
            to={main + "clube"}
            className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-insigne-orange/35
                       bg-insigne-orange/[0.07] px-4 py-3 mb-6 hover:bg-insigne-orange/[0.12] transition-colors"
          >
            <span className="inline-flex items-center gap-1.5 text-sm font-bold text-white">
              <i className="bi bi-stars text-insigne-orange" />
              {fraseDoClube(vitrine.precoCentavos, vitrine.dias)}
            </span>
            {/* "Conhecer" sem seta: a barra inteira já é um link, e a seta
                prometia um segundo destino que não existe. */}
            <span className="text-xs text-white/60">
              {vitrine.acervo} livros no Clube Insigne.
              <span className="text-insigne-orange font-semibold"> Conhecer</span>
            </span>
          </Link>
        )}

        {/* --topo-fixo = onde está, NESTE instante, a borda de baixo do
            cabeçalho do site. É a conta de que a faixa de "Resultados" precisa
            para grudar rente a ele, e mora aqui no `div` de fora porque até
            11/09/2026 a coluna de filtros dividia a mesma conta com ela.

            De onde saem as parcelas:
              --nav-h   = altura do cabeçalho MAIS a folga que toda página usa
                          como padding do topo (224px a partir de 768px, para um
                          cabeçalho de ~184px — a conta está no App.css);
              --nav-topo = 0 quando o cabeçalho está à vista e -(--nav-h) quando
                          ele sai de cena ao rolar para baixo (useNavbarOculta).
                          É o mecanismo que o leitor já usa (Leitor.css); somar
                          as duas é o que faz um bloco grudado acompanhar o
                          cabeçalho em vez de deixar um buraco onde ele estava;
              -40px      = a folga embutida em --nav-h. Sem descontá-la, a faixa
                          grudaria 40px ABAIXO do cabeçalho e a grade apareceria
                          rolando nessa fresta. Só o valor de 768px+ importa:
                          nada aqui gruda no celular.
              max(0px, …) = piso, para a conta nua (-40px com o cabeçalho
                          escondido) não levar a faixa para fora da tela com o
                          título decepado.

            O RESPIRO DO TOPO NÃO MORA MAIS NO `top`. Ele já foi `max(16px, …)`,
            e os 16px resolviam o "Categoria grudado no teto" criando um defeito
            pior: uma fresta TRANSPARENTE de 16px acima da faixa, por onde a
            grade aparecia rolando (relato do cliente com o print do catálogo).
            Fundo não pinta onde a caixa não está.
            Agora o `top` é 0 e o respiro é `md:pt-4` DENTRO da caixa, com
            `md:-mt-4` devolvendo os mesmos 16px ao fluxo: a caixa encosta na
            borda da janela — e é o FUNDO dela que encosta, não o texto, que
            continua 16px abaixo.

            Era `top-[185px]` chumbado nos dois arquivos: acertava o cabeçalho à
            vista e, quando ele sumia, deixava 185px de tela vazia acima da
            faixa — que é o defeito que este cálculo veio corrigir. */}
        {/* A GRADE OCUPA A LARGURA INTEIRA desde 11/09/2026: a coluna de
            filtros de 260px virou o popup do cabeçalho (ver BotaoCategorias).
            Este `div` sobreviveu à saída dela porque é ele quem define
            `--topo-fixo` — a altura em que a faixa de cabeçalho gruda, medida a
            partir do cabeçalho do site, que sobe e desce com a rolagem. */}
        <div className="md:[--topo-fixo:max(0px,calc(var(--nav-h)_+_var(--nav-topo)_-_40px))]">
          {/* =================== CONTEÚDO =================== */}
          <main className="min-w-0">
            {/* ====== A FAIXA DE CABEÇALHO DO CATÁLOGO ======
                Título, contagem, busca, Categorias e os chips do que está
                filtrado. No desktop ela ACOMPANHA A ROLAGEM: quem chegava na
                altura do livro 80 ficava sem contagem, sem busca e sem saber
                que filtro estava ligado — tinha de rolar tudo de volta para
                descobrir. Agora que a coluna de filtros saiu, ela é O ÚnICO
                lugar onde esses controles existem.

                O fundo opaco e a camada própria (`md:z-30`, em GRUDA_NO_TOPO)
                não são enfeite: sem fundo as capas passariam por baixo do texto
                e a faixa ficaria ilegível; sem camada, a grade — que vem depois
                no DOM — pintaria por cima dela. O 30 fica ACIMA do que os cards
                usam internamente (até 20) e bem ABAIXO do cabeçalho do site
                (z-101) e dos modais (z-9998+), que continuam cobrindo tudo.

                `md:-mt-4 md:pt-4`: os 16px de respiro do topo. A margem
                negativa devolve os mesmos 16px ao fluxo, senão o título
                nasceria 16px abaixo de onde nascia antes. É este `pt` que faz o FUNDO da
                caixa chegar até a borda da janela enquanto o texto para 16px
                antes; com o respiro no `top`, aqueles 16px eram fresta
                transparente e a grade aparecia rolando por ela.

                Nada disso vale no celular — cabeçalho preso lá come a altura
                útil da tela, que já é pouca depois do cabeçalho do site. Daí o
                prefixo `md:` em TODAS as classes deste bloco.
                A CAIXA É MAIS LARGA QUE A COLUNA, e isso é o conserto de um
                vazamento: a sombra sólida do card se projeta para FORA dele
                (7px no hover, ver CardDestaque.js), e o card da última coluna
                termina exatamente na borda direita desta coluna. Com o fundo
                cobrindo só a largura da coluna, essa sombra aparecia como um
                risco vertical escuro à direita da faixa toda vez que a fileira
                passava por baixo dela — foi o print do cliente em 10/09/2026.
                `md:-mx-3 md:px-3` estica o fundo 12px para cada lado e devolve
                os mesmos 12px ao conteúdo, que não sai do lugar. Os 12px cabem
                na folga de 32px do `px-8` da página, então nada disso cria
                rolagem horizontal.

                O RESPIRO DE BAIXO É MARGEM, e precisa continuar sendo. Ele já
                foi `md:pb-6` — padding, portanto pintado com o fundo da caixa —
                numa tentativa de tapar a sombra que aparecia à direita. Era o
                diagnóstico errado: aquela sombra vazava PELA LATERAL (ver
                `md:-mx-3`), não por baixo. E o padding opaco cobriu a FITA do
                primeiro card da grade, que se projeta 10px ACIMA do topo dele
                (`-top-2.5` em CardDestaque.js) e nasce dentro dos 24px logo
                abaixo da faixa. Como a faixa é `z-30` e a fita é `z-20` dentro
                do card, quem ganha é a faixa: o "+ E-BOOK" aparecia cortado ao
                meio, parado, sem ninguém rolar a página.
                Margem não pinta — e é exatamente por isso que ela serve aqui.
                Conteúdo passando por baixo da faixa ao rolar é o que toda
                faixa fixa faz; o defeito era só o primeiro card nascer coberto. */}
            <div ref={faixaSentinelaRef} aria-hidden="true" className="h-0" />
            <div
              ref={faixaRef}
              className={"mb-6 md:-mt-4 md:pt-4 md:-mx-3 md:px-3 md:bg-insigne-bg " +
                         GRUDA_NO_TOPO + " " + (esconderFaixa ? CLASSES_ESCONDIDA : "")}
            >
              {/* A régua de 2px na cor da sombra: era uma hairline de 1px,
                  a última da página depois de as divisórias do site passarem
                  para 2px. */}
              <div className="md:pb-3 md:border-b-2 md:border-insigne-sombra">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  {/* O <h2> ENCOLHEU quando a página ganhou um <h1> (16/09/2026).
                      "Resultados" a 24px disputava o título da página que agora
                      está logo acima, e ainda por cima era a palavra menos útil
                      da faixa: quem olha aqui quer o NÚMERO. Ele virou rótulo e
                      a contagem virou a linha que se lê — a mesma troca de
                      pesos, sem tirar o título da estrutura da página. */}
                  <h2 className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-white/45">
                    Resultados
                  </h2>
                  <p className="mt-1 text-[15px] font-bold text-white">
                    {loading ? (
                      "Carregando o catálogo..."
                    ) : ordenados.length === 0 ? (
                      "Nenhum livro encontrado"
                    ) : (
                      // A FAIXA da página, e não a contagem do que está na tela:
                      // com paginação, "Mostrando 12 de 199" não diz QUAIS 12.
                      <>
                        Mostrando {inicio + 1}-{Math.min(inicio + LIVROS_POR_PAGINA, ordenados.length)} de{" "}
                        {ordenados.length} livro{ordenados.length === 1 ? "" : "s"}
                        {buscaURL ? ` para "${buscaURL}"` : ""}
                      </>
                    )}
                  </p>
                </div>

                {/* ===== A BUSCA DESTA PÁGINA (pedido do dono, 11/09/2026) =====
                    Procurar um título exigia subir até a barra do cabeçalho do
                    site, que some ao rolar o catálogo. Aqui ela mora na faixa
                    que acompanha a rolagem, ao lado da contagem que ela muda.
                    A ORDEM NA FILEIRA: no celular a busca desce para a linha
                    de baixo (`order-[99]`); do tablet para cima vale a ordem do
                    código — título, busca e, À DIREITA dela, os filtros (pedido
                    do dono, 11/09/2026). Era `order-last md:order-none`, e o
                    Bootstrap anulava a segunda metade: o `.order-last` dele
                    tem `!important`, então a busca ficava por último também no
                    desktop e os filtros apareciam à esquerda dela. Valor
                    arbitrário não tem homônimo no Bootstrap. */}
                <div className="relative flex-1 min-w-[min(100%,220px)] order-[99] md:order-[0]">
                  <i className="bi bi-search absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-white/40 pointer-events-none" />
                  <input
                    type="search"
                    value={textoBusca}
                    onChange={(e) => {
                      setTextoBusca(e.target.value);
                      const q = new URLSearchParams(location.search);
                      if (e.target.value) q.set("busca", e.target.value);
                      else q.delete("busca");
                      navigate(`${location.pathname}${q.toString() ? "?" + q.toString() : ""}`, { replace: true });
                    }}
                    placeholder="Buscar por título ou autor..."
                    aria-label="Buscar no catálogo"
                    className="foco-laranja w-full rounded-md border border-line bg-white/5 py-2 pl-10 pr-3
                               text-sm text-white placeholder:text-white/40 outline-none
                               transition-colors focus:border-insigne-orange/50 focus:bg-white/[0.07]"
                  />
                </div>

                {/* AS CATEGORIAS, no lugar onde ficava o seletor de ordem.
                    O mesmo botão serve às duas telas: popup no desktop,
                    bottom-sheet no celular (ver BotaoCategorias).

                    O SELETOR DE ORDEM SAIU (pedido do dono, 11/09/2026):
                    quatro opções para um catálogo que se percorre por categoria
                    e por busca, e a que a pessoa mais usaria ("Mais Populares")
                    é a que o card não deixa perceber. A lista continua saindo
                    do mais novo para o mais antigo, e `?ordem=` continua
                    valendo — os atalhos "Mais Vendidos" e "Novidades" do painel
                    de categorias mandam por ali. */}
                <BotaoCategorias
                  filtros={propsFiltros}
                  aoAbrirNoCelular={() => setPainelAberto(true)}
                />
              </div>

              {/* Chips dos filtros aplicados. Vêm junto na faixa que gruda: são
                  a única pista do que está filtrado com o popup fechado, e
                  deixá-los rolar para longe era metade do problema. */}
              {chips.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  {chips.map((chip) => (
                    <button
                      key={chip.id}
                      onClick={chip.limpar}
                      className="foco-laranja inline-flex items-center gap-2 rounded-controle border border-insigne-orange/60
                                 bg-insigne-orange/10 pl-3 pr-2 py-1 text-xs text-insigne-orange
                                 hover:bg-insigne-orange/20 transition-colors"
                    >
                      {chip.texto}
                      <i className="bi bi-x-lg text-[10px]" />
                    </button>
                  ))}
                  {chips.length > 1 && (
                    <button
                      onClick={limparTudo}
                      className="text-xs text-white/55 underline hover:text-white transition-colors ml-1"
                    >
                      limpar tudo
                    </button>
                  )}
                </div>
              )}
              </div>
            </div>

            {/* Loading / Empty / Grid */}
            {loading ? (
              <GradeSkeleton quantidade={LIVROS_POR_PAGINA} />
            ) : ordenados.length === 0 ? (
              <div className="flex flex-col items-center justify-center text-center py-20">
                <CaixaVazia />
                <h3 className="titulo-display text-[2rem] mt-6">
                  Nada encontrado para "{buscaURL || categoriaSel || "esses filtros"}"
                </h3>
                <p className="text-white/50 mt-1">Tente outra busca ou remova alguns filtros.</p>
                {chips.length > 0 && (
                  // Era contorno vazado que se PREENCHIA de laranja no hover — o
                  // inverso do vocabulário da casa. `.btn-solido` já traz fundo,
                  // borda tracejada e a inversão (esvazia no hover), então aqui
                  // sobram só forma e espaçamento; repetir bg-/border-/hover:bg-
                  // por cima brigaria com a classe.
                  <button
                    onClick={limparTudo}
                    className="btn-solido mt-5 rounded-controle px-5 py-2 text-sm font-semibold"
                  >
                    Limpar filtros
                  </button>
                )}
              </div>
            ) : (
              <>
                {/* Card único do site. `gap-y-12` (48px, número que o Bootstrap
                    não declara) existe porque o botão de carrinho do card se
                    projeta para FORA dele, com `translate-y-1/2` — ver
                    CardDestaque.js. Sem essa folga a metade que vaza pousaria
                    em cima do card da linha de baixo. */}
                <RevealStagger className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-x-4 gap-y-12 orfa-ao-centro">
                  {itensPagina.map((livro) => (
                    <RevealStagger.Item key={livro.id} className="h-full">
                      <CardDestaque livro={livro} onQuickView={setEspiando} />
                    </RevealStagger.Item>
                  ))}
                </RevealStagger>

                {/* Mesmo controle de /autores. Ele já volta ao topo a cada
                    troca e se esconde sozinho quando só há uma página. */}
                <Pagination pagina={paginaAtual} totalPaginas={totalPaginas} onChange={irParaPagina} />
              </>
            )}
          </main>
        </div>
      </div>

      {/* Bottom-sheet dos filtros do celular. Fica FORA do `flex` do layout de
          propósito: dentro daquele container ele herdaria o contexto de
          empilhamento da coluna e o `z-[9998]` passaria a valer só ali dentro,
          ficando por baixo do header. Recebe o mesmo `propsFiltros` do popup —
          é o que garante que os dois painéis marquem sempre a mesma coisa.
          `total` é a contagem JÁ filtrada: como o filtro é aplicado na hora, o
          número no rodapé é o retorno imediato de cada toque, com a grade
          escondida atrás da folha. */}
      <DrawerFiltros
        aberto={painelAberto}
        onFechar={() => setPainelAberto(false)}
        total={ordenados.length}
        onLimparTudo={limparTudo}
        filtros={propsFiltros}
      />

      {/* Espiada rápida: fica fora da grade para o modal cobrir a página toda */}
      <QuickView livro={espiando} onClose={() => setEspiando(null)} />
    </div>
  );
}

export default GridProdutos;
